import { List, useTable } from "@refinedev/antd";
import { CanAccess, useCan } from "@refinedev/core";
import { Button, DatePicker, Segmented, Space, Statistic, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { Link } from "react-router";

import { DATE_FORMAT, DATE_TIME_FORMAT, PAYOUT_STATUS, formatKurus } from "../labels";
import type { PayoutItem } from "../types";
import { PayoutPayDialog } from "./payout-pay-dialog";

const VIEW_OPTIONS = [
  { value: "requested", label: "Ödenecek" },
  { value: "paid", label: "Ödenen" },
  { value: "all", label: "Tümü" },
];

function filterValue(filters: ReturnType<typeof useTable>["filters"], field: string) {
  const filter = filters.find((item) => "field" in item && item.field === field);
  return (filter?.value as string | undefined) ?? "";
}

/**
 * /payouts: tutors' withdrawal requests, paid by bank transfer on Thursdays (permission
 * `payouts.view`). The full IBAN opens one request at a time for those who pay
 * (`payouts.markPaid`); each opening is recorded in the audit log.
 */
export function PayoutsPage() {
  const { data: canSeeTutors } = useCan({ resource: "tutors", action: "list" });
  const [paying, setPaying] = useState<PayoutItem | null>(null);
  const { tableProps, tableQuery, filters, setFilters } = useTable<PayoutItem>({
    resource: "payouts",
    pagination: { mode: "server", pageSize: 25 },
    filters: { initial: [{ field: "view", operator: "eq", value: "requested" }] },
    syncWithLocation: true,
  });
  const view = filterValue(filters, "view") || "requested";
  const scheduledFor = filterValue(filters, "scheduledFor");
  const sumKurus = (tableQuery.data as { sumKurus?: number } | undefined)?.sumKurus ?? 0;

  const setFilter = (field: "view" | "scheduledFor", value: string) =>
    setFilters(
      [
        { field: "view", operator: "eq", value: field === "view" ? value : view },
        {
          field: "scheduledFor",
          operator: "eq",
          value: field === "scheduledFor" ? value : scheduledFor,
        },
      ],
      "replace",
    );

  return (
    <List title="Eğitmen ödemeleri">
      <Typography.Paragraph type="secondary">
        Ödemeler Perşembe günleri Wise ile eğitmenin banka hesabına gönderilir: Çarşamba'ya kadar
        gelen talepler o Perşembe, sonrakiler bir sonraki Perşembe ödenir. Gönderimden sonra talebi
        ödendi olarak işaretle.
      </Typography.Paragraph>
      <Space wrap align="center" style={{ marginBottom: 16 }}>
        <Segmented
          options={VIEW_OPTIONS}
          value={view}
          onChange={(value) => setFilter("view", String(value))}
        />
        <DatePicker
          allowClear
          placeholder="Ödeme günü"
          format={DATE_FORMAT}
          value={scheduledFor ? dayjs(scheduledFor) : null}
          // Transfers happen on Thursdays only.
          disabledDate={(date) => date.day() !== 4}
          onChange={(date) => setFilter("scheduledFor", date ? date.format("YYYY-MM-DD") : "")}
        />
        <Statistic
          title={`Toplam (${tableQuery.data?.total ?? 0} talep)`}
          value={formatKurus(sumKurus)}
          valueStyle={{ fontSize: 18 }}
        />
      </Space>
      <Table {...tableProps} rowKey="id" scroll={{ x: "max-content" }}>
        <Table.Column<PayoutItem>
          title="Eğitmen"
          dataIndex="tutor"
          render={(tutor: PayoutItem["tutor"]) =>
            canSeeTutors?.can ? <Link to={`/tutors/${tutor.id}`}>{tutor.name}</Link> : tutor.name
          }
        />
        <Table.Column<PayoutItem>
          title="Tutar"
          dataIndex="amountKurus"
          render={(value: number) => <Typography.Text strong>{formatKurus(value)}</Typography.Text>}
        />
        <Table.Column<PayoutItem>
          title="Ödeme günü"
          dataIndex="scheduledFor"
          render={(value: string) => dayjs(value).format(DATE_FORMAT)}
        />
        <Table.Column<PayoutItem>
          title="Talep"
          dataIndex="requestedAt"
          render={(value: string) => dayjs(value).format(DATE_TIME_FORMAT)}
        />
        <Table.Column<PayoutItem>
          title="IBAN"
          dataIndex="ibanLast4"
          render={(value: string) => `TR•• •••• ${value}`}
        />
        <Table.Column<PayoutItem>
          title="Durum"
          dataIndex="status"
          render={(value: string, payout) => (
            <Space direction="vertical" size={0}>
              <Tag color={PAYOUT_STATUS[value]?.color}>{PAYOUT_STATUS[value]?.label}</Tag>
              {payout.paidAt ? (
                <Typography.Text type="secondary">
                  {dayjs(payout.paidAt).format(DATE_TIME_FORMAT)}
                </Typography.Text>
              ) : null}
            </Space>
          )}
        />
        <Table.Column<PayoutItem>
          title=""
          key="actions"
          render={(_, payout) =>
            payout.status === "REQUESTED" ? (
              <CanAccess resource="payouts" action="pay">
                <Button size="small" onClick={() => setPaying(payout)}>
                  Gönderim bilgileri
                </Button>
              </CanAccess>
            ) : null
          }
        />
      </Table>

      <PayoutPayDialog
        payout={paying}
        onClose={() => setPaying(null)}
        onPaid={() => tableQuery.refetch()}
      />
    </List>
  );
}
